// Our local storage plugin
import Locally from 'locallyjs';

////////////////////////////////////////////
// Netx global namespace
////////////////////////////////////////////
// Setup global namespace
/**
 * @namespace
 * @static
 */
const Netx = {
	version: '2.22.27.c9cf8853',
	/**
	 * @namespace
	 * @static
	 */
	custom: {},
	getUserLevel: () => -1,
	getPreference: () => false,
	setPreference: () => true,
	/**
	 * Various DAM-related static properties
	 * @memberOf Netx
	 * @namespace
	 * @static
	 * @property {array} states - A list of states (provided by the DAM)
	 * @property {array} countries - A list of countries (provided by the DAM)
	 * @property {array} attributes - A list of i18n labels for asset properties
	 * @property {array} assetAttributes.ids - Map from label to IDs for asset metadata types
	 * @property {array} assetAttributes.numberTypes - Labels, warnings & validators for the various numeric sub-types
	 * @property {array} assetAttributes.types - Map from IDs to labels for asset metadata types
	 * @property {array} search - Labels and metadata for the various fields of a searchAssetListObjects() RPC call, as used by Netx.Collections.Assets & others
	 * @todo refactor 'attributes' to 'attributeLabels'
	 * @todo assetAttributes.ids and .types are redundant; refactor.
	 * @private
	 */
	properties: {},
	/**
	 * Netx.router is our instance of Backbone.Router
	 * @memberOf Netx
	 * @namespace
	 * @static
	 */
	router: {
		routes: {},
	},
	routerReady: false,
	//https://github.com/ozantunca/locally
	store: new Locally.Store({
		// DAM-19220 - set to false
		compress: false,
	}),
	/**
	 * We create a dummy Netx.user.profile here describing an unauthenticated user;
	 * This will be overwritten with real user data when we authenticate.
	 * @memberOf Netx
	 * @namespace
	 * @static
	 */
	user: {},
	/**
	 * When controllers need a View to have a public name -- i.e. when several listeners need to operate on the same View --
	 * the controller can put that Views in Netx.views; it can be named anything, but by convention it's usually given the
	 * name of the template, which by convention is the View class name lowercased.
	 * For instance, Netx.views.assetDetailView is an instance of Netx.Views.AssetDetailView used by the asset controller.
	 * @memberOf Netx
	 * @namespace
	 * @static
	 */
	views: {},
	/**
	 * Netx.Collections namespace
	 * @memberof Netx
	 * @namespace
	 * @static
	 */
	Collections: {},
	/**
	 * Netx.Models namespace maintains model class definitions
	 * @memberof Netx
	 * @namespace
	 * @static
	 */
	Models: {},
	/**
	 * Netx.Routers namespace
	 * @memberof Netx
	 * @namespace
	 * @static
	 */
	Routers: {},
	/**
	 * Netx.ShareableTypes holds objects describing things that the DAM can share, along with verbiage to describe those things.
	 * @memberof Netx
	 * @namespace
	 * @static
	 */
	ShareableTypes: {},
	/**
	 * Netx.Views namespace
	 * @memberof Netx
	 * @namespace
	 * @static
	 */
	Views: {},
};
export default Netx;
window.Netx = Netx;

// Some broad configurations that can not live in the app module
import opts from './config';
window.custom = opts;
////////////////////////////////////////////
// !Netx global namespace
////////////////////////////////////////////

////////////////////////////////////////////
// App utility libraries
////////////////////////////////////////////
// Underscore overrides/enhancements
import './lib/underscore-netx';

// jQuery overrides/enhancements
import './lib/jquery-netx';

// Load in backbone
import './lib/backbone-netx';
// Create global event dispatcher
export const dispatcher = (window.dispatcher = {...Backbone.Events});

// Modernizr
import 'core-libs/modernizr/modernizr';
import 'core-libs/modernizr-tests-0.0.1';

// Polyfills
import 'core-libs/polyfills/polyfills-netx';

// Hammerjs (for cross platform gestures)
import Hammer from 'hammerjs';
window.Hammer = Hammer;
////////////////////////////////////////////
// !App utility libraries
////////////////////////////////////////////

////////////////////////////////////////////
// Netx module intialization
////////////////////////////////////////////
/**
 * Modules already initialized indexed by namespace
 * @type {array}
 */
const initializedModules = [];
/**
 * Modules currently initializing indexed by namespace and storing the promise to the original initialization call for that module
 * @type {object}
 */
const initializingModules = {};

/**
 * Module initialization helper
 * @function
 * @global
 * @param {array|object} modules Module(s) to initialize
 * @param {object} opts Options
 */
// https://stackoverflow.com/questions/37787123/is-it-possible-to-let-webpacks-system-import-use-ajax-for-progress-events
// One day we could look into extending the webpack functionality with our own plugin to allow for XHR requests of
// all of our bundles - then we could use a `progress` event on it and dispatch refined progress
const initializeModule = function (modules, opts = {}) {
	// Convert to array if not already
	modules = _.isArray(modules) ? modules : [modules];

	return new Promise((resolve, reject) => {
		var idx = 0,
			newModules = 0,
			routesToAdd = {},
			total = modules.length;

		// Go on to next module (or resolve)
		function next() {
			idx++;
			idx < total ? init() : finished();
		}

		// Initialize module
		function init() {
			var m = modules[idx],
				namespace = m.namespace;

			// Check if already initialized
			if (_.contains(initializedModules, namespace)) {
				return next();
			}

			// Are we already initializing this module - listen for its completion
			// and then continue on
			if (!_.isUndefined(initializingModules[namespace])) {
				return initializingModules[namespace].then(next);
			}

			// We are initializing this module now
			dispatcher.trigger(
				'netx:initializing:module',
				namespace,
				(idx / total) * 100,
				opts.deferred,
				opts.loadUrl,
			);

			// Store promise
			initializingModules[namespace] = m.initialize().then(
				function (routes) {
					// If the module was deferred remove its wildcard route now
					if (
						opts.deferred &&
						Netx.router.routes[m.namespace + '(/*anything)']
					) {
						delete Netx.router.routes[m.namespace + '(/*anything)'];
					}
					// Add routes to queue
					routes && _.extend(routesToAdd, routes);
					// Store namespace in modules initialized array
					initializedModules.push(namespace);
					// A new module was initialized
					newModules++;
					// Remove initialize promise from the initializing array
					delete initializingModules[namespace];
					// Dispatche event
					dispatcher.trigger(
						'netx:initialized:module',
						namespace,
						((idx + 1) / total) * 100,
						opts.deferred,
						opts.loadUrl,
					);
					// Next module or resolve
					next();
				},
				(err) => {
					console.error(err);
				},
			);
		}

		// When all modules are initialized
		function finished() {
			// Defer to allow anything hooked on any of the module initialized promises to respond
			_.defer(function () {
				// Only if new modules were intialized
				if (newModules) {
					// Add all new routes
					Netx.router.addRoutes(routesToAdd);
					// Load route url when all modules are loaded
					opts.loadUrl && Netx.routerReady && Backbone.history.loadUrl();
				}
			});
			resolve(modules);
		}

		// Start
		init();
	});
};

/**
 * @event module:netx#netx:load:module
 * @param {array|object} modules Module object or an array of module objects
 * @param {object} opts Options
 */
dispatcher.on('netx:load:module', function (modules, opts) {
	opts = opts || {};
	initializeModule(
		modules,
		_.extend(
			{
				deferred: false,
				loadUrl: false,
			},
			_.pick(opts, 'deferred', 'loadUrl'),
		),
	).then(
		function () {
			_.isFunction(opts.initialized) && opts.initialized();
		},
		function (err) {
			_.isFunction(opts.error) && opts.error(err);
		},
	);
});

/**
 * @event module:netx#netx:load:deferred:module
 * @param {array|object} modules Module object or an array of module objects
 * @param {object} opts Options
 */
dispatcher.on('netx:load:deferred:module', function (modules, opts) {
	dispatcher.trigger(
		'netx:load:module',
		modules,
		_.extend({deferred: true}, opts),
	);
});
////////////////////////////////////////////
// !Netx module intialization
////////////////////////////////////////////

/*******
 * The following logic builds the Netx.URL object
 * which contains all relevant path information
 * such as host, protocol, query string, base, api
 * endpoint, etc.
 *
 * TODO: could this go in configure.js ?
 */
// Set API version
let api = (Netx.api = {});
// Allow an instance to override the default API version via config.js
api.version = 'v' + (!_.isUndefined(opts.API) ? opts.API : '1.2');
// API endpoint
api.endpoint = `/x7/${api.version}/json`;
export const URL = (Netx.URL = {});
// Set the global host
URL.host = window.location.host;
// Set the global location
URL.location = window.location.href.toString();
// Set the global protocol
URL.protocol = URL.location.split(URL.host)[0];
// Set matching socket protocol, ws: or wss:
URL.websocketProtocol = URL.protocol.replace(/http/, 'ws');
// Set the query string variables
URL.queryString = URL.location.split(URL.host)[1];
// Set the base directory
URL.baseDir = window.location.pathname;
// Set the base url
URL.base = URL.protocol + URL.host + URL.baseDir;
// Set the api end point
URL.apiEndpoint = api.endpoint;
// Set the api url
URL.api = Netx.URL.protocol + URL.host + URL.apiEndpoint;
// Set the default location of the rpc proxy
URL.proxyApiEndpoint = '/core/proxy/';
URL.proxyApi = URL.protocol + URL.host + URL.proxyApiEndpoint;
// Upload-specific settings.
URL.uploadEndpoint = '/servlet/FileUploader';
//Netx.URL.assetRetrieval = "/servlet/AssetRetrieval"; // deprecated
URL.upload = URL.protocol + URL.host + URL.uploadEndpoint;
// Set the deprecated api key to the new URL object ref
// Asset retrieval servlet (for zoom)
URL.assetEndpoint = '/file/asset';
URL.asset = URL.protocol + URL.host + URL.assetEndpoint;

URL.assetFileApiEndpoint = `${URL.protocol}${URL.host}/api/file/asset/`;

const themeName = (Netx.themeName =
	window.location.pathname.match(/\/portals\/(.*)\//)?.[1] || 'netx');

// Branding
// Login background (only for netx theme currently)
URL.loginBackgroundEndpoint = '/theme/netx/background';
URL.loginBackground = URL.protocol + URL.host + URL.loginBackgroundEndpoint;

// Watermark
URL.watermarkEndpoint = `/theme/${themeName}/watermark`;
URL.watermark = `${URL.protocol}${URL.host}${URL.watermarkEndpoint}`;

// CSS Properties (vars)
URL.customCssPropertiesEndpoint = `/theme/${themeName}/cssCustomProperties`;
URL.customCssProperties =
	URL.protocol + URL.host + URL.customCssPropertiesEndpoint;

// Logos (only for netx theme currently)
URL.logoLightEndpoint = '/theme/netx/logo-light';
URL.logoDarkEndpoint = '/theme/netx/logo-dark';
URL.logoLight = URL.protocol + URL.host + URL.logoLightEndpoint;
URL.logoDark = URL.protocol + URL.host + URL.logoDarkEndpoint;

// This is to holdover portal until both `netx` and `portal` use the same branding mechanism
URL.themeLogo = themeName !== 'netx' ? `/theme/${themeName}/logo` : '';

api = URL.api;

URL.websocket = `${URL.websocketProtocol}${URL.host}/websocket`;
